import { Box, List, ListItem, ListItemIcon, ListItemText, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchOpenEncounters, searchEncounters } from "apiClients/encounters";
import PendingIcon from "components/common/SVG/Pending";
import { Person } from "components/common/SVG/Person";
import ScheduleIcon from "components/common/SVG/Schedule";
import CustomTooltip from "components/common/Tooltips/Custom";
import SkeletonWrapper from "components/common/Wrappers/Skeleton";
import usePermissions from "hooks/usePermission";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setEncounterOpenCountDetails } from "redux-store/features/Encounter";
import { SkeletonPropsStyleGenerator } from "styles/Common/SkeletonStyle";
import "styles/index.css";
import { ReduxState } from "../../../constants/config/appointmentsDashboard.type";

const SidebarRoot = styled(Box)(({ theme }) => ({
  height: "100%",
  "& .MuiButtonBase-root.MuiListItem-root": {
    paddingLeft: "10px !important",
  },
  "& .MuiListItemIcon-root": {
    width: "40px",
    height: "100%",
    "& .MuiSvgIcon-root": {
      width: "100%",
      height: "35px",
      [`@media (max-width: ${theme.breakpoints.values.lg - 1}px)`]: {
        height: "26px",
      },
    },
  },
}));

const IconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  fill: isActive ? theme.palette.themePrimary.tertiary : "inherit",
}));

interface SidebarProps {
  /** Show icon + label text (mobile drawer mode) */
  showLabels?: boolean;
  /** Called after a nav item is clicked (used to close mobile drawer) */
  onItemClick?: () => void;
}

function Sidebar({ showLabels = false, onItemClick }: SidebarProps = {}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const [prevUrl, setPrevUrl] = useState(null);
  const [active, setActive] = useState(() => {
    return localStorage.getItem("active") || routerLocation.pathname;
  });
  const { multipleActiveProviders } = useSelector((state: ReduxState) => state.locations);

  const { openEncountersCount, openEncountersProcessing } = useSelector((state: ReduxState) => state.encounter);

  const { loggedIn, groups, userId: loggedInUserId } = useSelector((state: ReduxState) => state.auth);
  const isProviderOnly = groups?.includes("Provider") && !groups?.includes("Practice Administrator");

  const {
    data: openEncounterData,
    isFetching: isFetchingOpenEncounter,
    refetch,
  } = useQuery({
    enabled: loggedIn && hasPermission("view_encounter"),
    queryKey: ["openEncountersOnce", multipleActiveProviders], // Same key to use cached data
    queryFn: async () => {
      //if the All providers are selected then call open encounters api which will fetch encounters for all providers
      if ((isProviderOnly || multipleActiveProviders?.length) && multipleActiveProviders[0]?.value !== "All Providers") {
        return searchEncounters({
          search_query: "",
          user_id: multipleActiveProviders?.length ? multipleActiveProviders?.map((provider) => provider.value)?.join(",") : loggedInUserId,
        });
      } else {
        return fetchOpenEncounters(0);
      }
    },
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchInterval: 2 * 60 * 1000,
  });

  useEffect(() => {
    if (prevUrl === "/encounters" && routerLocation.pathname !== "/encounters") {
      dispatch(
        setEncounterOpenCountDetails({
          count: 0,
          isLoading: true,
        })
      );
      refetch();
    }

    setPrevUrl(routerLocation.pathname);
  }, [routerLocation, prevUrl]);

  useEffect(() => {
    if (!isFetchingOpenEncounter) {
      dispatch(
        setEncounterOpenCountDetails({
          count: openEncounterData?.count || 0,
          isLoading: isFetchingOpenEncounter,
        })
      );
    }
  }, [openEncounterData, isFetchingOpenEncounter]);

  const countBoxStyling = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    top: "15px",
    left: "11px",
    fontSize: "11px",
    height: "20px",
    width: "30px",
  };
  const upperArray = [
    {
      id: "encounters",
      IconComponent: () => (
        <Box sx={{ position: "relative" }}>
          <PendingIcon width="30" height="30" fill="rgb(45, 55, 72)" />
          {active !== "/encounters" && (
            <SkeletonWrapper
              condition={!openEncountersProcessing}
              props={[SkeletonPropsStyleGenerator("100%", "30px")]}
              sx={countBoxStyling}>
              <Box
                sx={{
                  ...countBoxStyling,
                  borderRadius: "50px",
                  background: theme.palette.primary.main,
                  color: theme.palette.background.paper,
                }}>
                {openEncountersCount || 0}
              </Box>
            </SkeletonWrapper>
          )}
        </Box>
      ),
      path: "/encounters",
      permission_name: "view_encounter",
      title: "Open Encounters",
    },
    {
      id: "appointments",
      permission_name: "view_appointment",
      IconComponent: ScheduleIcon,
      path: "/appointments",
      title: "Appointments",
    },
    {
      id: "patients",
      permission_name: "view_patient",
      IconComponent: Person,
      path: "/patients",
      title: "Patients",
    },
  ];

  useEffect(() => {
    setActive(routerLocation.pathname);
  }, [routerLocation.pathname]);

  useEffect(() => {
    localStorage.setItem("active", active);
  }, [active]);

  const handlePaths = (path) => {
    navigate(path);
    setActive(path);
    onItemClick?.();
  };

  return (
    <SidebarRoot>
      <List sx={{ alignContent: "center" }}>
        {upperArray
          ?.filter(({ permission_name }) => hasPermission(permission_name))
          ?.map(({ IconComponent, title, path, id }, index) => {
            const isActive = active === path;
            return (
              <ListItem
                key={index}
                data-testid={`sidebar-icon-${id}-id`}
                onClick={() => handlePaths(path)}
                sx={{
                  position: "relative",
                  marginTop: showLabels ? "2px" : { xs: "2px", lg: "10px" },
                  paddingTop: showLabels ? "10px" : { xs: "2px", lg: "8px" },
                  paddingBottom: showLabels ? "10px" : { xs: "2px", lg: "8px" },
                  paddingLeft: showLabels ? "16px !important" : undefined,
                  borderRadius: showLabels ? "8px" : undefined,
                  mx: showLabels ? "8px" : undefined,
                  backgroundColor: showLabels && isActive ? `${theme.palette.themePrimary.tertiary}18` : "transparent",
                  "&:hover": showLabels ? { backgroundColor: `${theme.palette.themePrimary.tertiary}12` } : undefined,
                  cursor: "pointer",
                }}>
                <ListItemIcon
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minWidth: showLabels ? "36px" : undefined,
                  }}>
                  {showLabels ? (
                    <IconWrapper isActive={isActive} data-testid={id}>
                      <IconComponent />
                    </IconWrapper>
                  ) : (
                    <CustomTooltip tooltipText={title} placement="right">
                      <IconWrapper isActive={isActive} data-testid={id}>
                        <IconComponent />
                      </IconWrapper>
                    </CustomTooltip>
                  )}
                </ListItemIcon>
                {showLabels && (
                  <ListItemText
                    primary={title}
                    sx={{
                      "& .MuiTypography-root": {
                        fontSize: "14px",
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? theme.palette.themePrimary.tertiary : theme.palette.text.primary,
                      },
                    }}
                  />
                )}
                {!showLabels && (
                  <Box
                    sx={{
                      position: "absolute",
                      right: "-3px",
                      top: "4px",
                      height: "40px",
                      border: isActive ? `3px solid ${theme.palette.themePrimary.tertiary}` : "",
                      borderRadius: "20px",
                    }}
                    data-testid={`${id}-active-bar`}
                  />
                )}
              </ListItem>
            );
          })}
      </List>
    </SidebarRoot>
  );
}

export default Sidebar;
